(() => {
  const i18n = (() => {
    try {
      return JSON.parse(document.getElementById('StylrI18n')?.textContent || '{}');
    } catch (_) {
      return {};
    }
  })();
  const translate = (key, fallback) => i18n[key] || fallback;
  const interpolate = (template, values) => Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{{ ${key} }}`, String(value)),
    template
  );

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const dialogTriggers = new Map();

  function closeDialog(dialog, restoreFocus = true) {
    if (!dialog || dialog.hidden) return;
    dialog.classList.remove('is-open');
    dialog.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => {
      dialog.hidden = true;
    }, 20);
    document.body.classList.remove('is-locked');

    const trigger = dialogTriggers.get(dialog);
    if (restoreFocus && trigger) trigger.focus();
  }

  function openDialog(dialog, trigger) {
    if (!dialog) return;
    document.querySelectorAll('[data-dialog].is-open').forEach((open) => closeDialog(open, false));
    dialogTriggers.set(dialog, trigger);
    dialog.hidden = false;
    dialog.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('is-locked');
    dialog.classList.add('is-open');
    const initial = dialog.querySelector('[data-dialog-initial-focus]') || dialog.querySelector(focusableSelector);
    if (initial) initial.focus({ preventScroll: true });
    window.requestAnimationFrame(() => {
      if (initial && document.activeElement !== initial) initial.focus({ preventScroll: true });
    });
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-dialog-trigger]');
    if (trigger) {
      event.preventDefault();
      const dialog = document.getElementById(trigger.getAttribute('data-dialog-trigger'));
      openDialog(dialog, trigger);
      return;
    }

    const close = event.target.closest('[data-dialog-close]');
    if (close) {
      const dialog = close.closest('[data-dialog]');
      const triggerForDialog = dialogTriggers.get(dialog);
      if (triggerForDialog) triggerForDialog.setAttribute('aria-expanded', 'false');
      closeDialog(dialog);
    }
  });

  document.addEventListener('keydown', (event) => {
    const dialog = document.querySelector('[data-dialog].is-open');
    if (!dialog) return;

    if (event.key === 'Escape') {
      const trigger = dialogTriggers.get(dialog);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      closeDialog(dialog);
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialog.querySelectorAll(focusableSelector));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.querySelectorAll('[data-localization-select]').forEach((select) => {
    select.addEventListener('change', () => select.form && select.form.submit());
  });

  function initProductVariantForm(form) {
    if (!form || form.dataset.variantReady === 'true') return;
    const section = form.closest('[data-product-section]');
    const variants = Array.from(form.querySelectorAll('[data-variant-input]'));
    if (!section || !variants.length) return;
    form.dataset.variantReady = 'true';
    const groups = Array.from(form.querySelectorAll('[data-variant-groups] .variant-group'));
    const price = section.querySelector('[data-product-price]');
    const initialPrice = section.querySelector('[data-product-base-price]')?.innerHTML || (price && price.innerHTML);
    const button = form.querySelector('[data-add-button]');
    const stickyButton = section.querySelector('[data-sticky-atc-button]');
    const stickyPrice = section.querySelector('[data-sticky-atc-price]');
    const stickySelection = section.querySelector('[data-sticky-atc-selection]');
    const status = form.querySelector('[data-selection-status]');
    const payment = form.querySelector('[data-product-payment]');
    const labels = {
      size: section.dataset.chooseSize || 'Choose size',
      options: section.dataset.chooseOptions || 'Choose options',
      unavailable: section.dataset.unavailable || 'Unavailable',
      required: section.dataset.selectionRequired || 'Please choose your options.'
    };
    // Keep the existing variant radio/data contract for Shopify and the bundle adapter.
    const valuesFor = (input) => {
      try { return JSON.parse(input.dataset.variantValues || '[]'); }
      catch (_) { return []; }
    };
    const readSelection = () => groups.map((group) => {
      const fixed = group.querySelector('[data-option-input]:not(input)');
      const picked = group.querySelector('input[data-option-input]:checked');
      return fixed ? fixed.dataset.optionValue : picked ? picked.dataset.optionValue : null;
    });
    let state;
    let lastVariantId = null;

    const focusMissing = () => {
      const pending = groups.find((_, index) => state.selected[index] == null) || groups[0];
      if (status) {
        status.hidden = false;
        status.textContent = state.complete ? labels.unavailable : labels.required;
      }
      if (!pending) return;
      pending.setAttribute('aria-invalid', 'true');
      pending.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      pending.querySelector('input[data-option-input]')?.focus({ preventScroll: true });
    };

    const sync = (updateUrl = false, announce = true) => {
      const selected = readSelection();
      const complete = selected.every((value) => value != null);
      const variant = complete ? variants.find((input) => {
        const values = valuesFor(input);
        return !groups.length || selected.every((value, index) => values[index] === value);
      }) : null;
      const available = Boolean(variant && variant.dataset.available === 'true');
      state = { selected, complete, variant, available };
      form.dataset.selectionComplete = String(complete && Boolean(variant));
      form.dataset.selectionAvailable = String(available);
      // A native form.submit() bypasses submit listeners. Disabling every id until
      // the selection is valid also prevents that route from posting a default SKU.
      variants.forEach((input) => {
        input.checked = input === variant;
        input.disabled = input !== variant || !available;
      });
      form.querySelectorAll('[data-option-property]').forEach((input) => {
        input.value = selected[Number(input.dataset.optionIndex)] || '';
        input.disabled = !complete || !variant;
      });
      groups.forEach((group, index) => {
        if (selected[index] != null) group.removeAttribute('aria-invalid');
        group.querySelectorAll('input[data-option-input]').forEach((input) => {
          const probe = selected.slice();
          probe[index] = input.dataset.optionValue;
          const possible = variants.some((candidate) => candidate.dataset.available === 'true' &&
            probe.every((value, position) => value == null || valuesFor(candidate)[position] === value));
          const label = input.nextElementSibling;
          if (label) label.toggleAttribute('data-unavailable', !possible);
        });
      });
      const pendingGroups = groups.filter((_, index) => selected[index] == null);
      const chooseLabel = pendingGroups.length && pendingGroups.every((group) => group.dataset.optionIsSize === 'true') ? labels.size : labels.options;
      const actionLabel = !complete ? chooseLabel : !variant ? labels.unavailable : available ? translate('addToCart', 'Add to cart') : translate('soldOut', 'Sold out');
      [button, stickyButton].forEach((action) => {
        if (!action) return;
        action.disabled = complete && !available;
        action.textContent = actionLabel;
      });
      if (price) {
        if (variant) {
          price.replaceChildren();
          const current = document.createElement('span');
          current.textContent = variant.dataset.price || '';
          price.append(current);
          if (variant.dataset.comparePrice) {
            const compare = document.createElement('del');
            compare.textContent = variant.dataset.comparePrice;
            price.append(compare);
          }
        } else {
          price.innerHTML = initialPrice;
        }
      }
      if (stickyPrice) stickyPrice.textContent = variant ? variant.dataset.price || '' : '';
      if (stickySelection) {
        stickySelection.textContent = groups.map((group, index) => selected[index] == null ? '' : `${group.dataset.optionName}: ${selected[index]}`).filter(Boolean).join(' · ');
        stickySelection.hidden = !stickySelection.textContent;
      }
      if (status) {
        status.hidden = !complete || available;
        status.textContent = complete && !available ? actionLabel : '';
      }
      if (payment) {
        payment.hidden = !available;
        payment.inert = !available;
        payment.setAttribute('aria-disabled', String(!available));
      }
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', variant.value);
        else url.searchParams.delete('variant');
        // Shopify option-value deep links must not override the newly chosen ID.
        url.searchParams.delete('option_values');
        window.history.replaceState(window.history.state, '', url);
      }
      const variantId = variant && available ? variant.value : '';
      if (announce && variantId !== lastVariantId) {
        lastVariantId = variantId;
        const input = variant || variants[0];
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return state;
    };

    const restoreFromUrl = () => {
      const requestedId = new URL(window.location.href).searchParams.get('variant');
      const linked = requestedId && variants.find((input) => input.value === requestedId);
      groups.forEach((group, index) => {
        const values = linked ? valuesFor(linked) : [];
        group.querySelectorAll('input[data-option-input]').forEach((input) => {
          input.checked = Boolean(linked && values[index] === input.dataset.optionValue);
        });
      });
      sync(false);
    };

    form.stylrProductSelection = { focusMissing, refresh: () => sync(false) };
    form.addEventListener('change', (event) => {
      if (!event.target.matches('input[data-option-input]')) return;
      sync(true);
    });
    form.addEventListener('click', (event) => {
      if (!event.target.closest('[data-add-button], [data-product-payment]')) return;
      sync(false, false);
      if (!state.complete || !state.available) {
        event.preventDefault();
        event.stopImmediatePropagation();
        focusMissing();
      }
    }, true);
    form.addEventListener('submit', (event) => {
      sync(false, false);
      if (!state.complete || !state.available || !form.checkValidity()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!state.complete || !state.available) focusMissing();
        else form.reportValidity();
      }
    }, true);
    window.addEventListener('popstate', restoreFromUrl);
    window.addEventListener('pageshow', (event) => { if (event.persisted) restoreFromUrl(); });
    form.addEventListener('reset', () => window.setTimeout(restoreFromUrl, 0));
    restoreFromUrl();
  }

  document.querySelectorAll('[data-product-form]').forEach(initProductVariantForm);

  function initCartForm(form) {
    if (!form || form.dataset.cartReady === 'true') return;
    form.dataset.cartReady = 'true';

    const trackedFields = Array.from(form.querySelectorAll('input[name="updates[]"], textarea[name="note"]'));
    const updateButton = form.querySelector('[data-cart-update]');
    const expressCheckout = form.querySelector('[data-cart-express]');
    const expressButtons = form.querySelector('[data-cart-express-buttons]');
    const dirtyMessage = form.querySelector('[data-cart-dirty-message]');
    const initialValues = new Map(trackedFields.map((field) => [field, field.defaultValue]));

    const syncCartState = () => {
      const dirty = trackedFields.some((field) => field.value !== initialValues.get(field));
      form.classList.toggle('is-dirty', dirty);

      if (updateButton) updateButton.disabled = !dirty;
      if (expressButtons) {
        expressButtons.inert = dirty;
      }
      if (expressCheckout) {
        expressCheckout.setAttribute('aria-disabled', String(dirty));
      }
      if (dirtyMessage) dirtyMessage.hidden = !dirty;
    };

    trackedFields.forEach((field) => {
      field.addEventListener('input', syncCartState);
      field.addEventListener('change', syncCartState);
    });

    form._stylrSyncCartState = syncCartState;
    syncCartState();
  }

  initCartForm(document.getElementById('CartForm'));
  document.addEventListener('shopify:section:load', (event) => {
    initCartForm(event.target.querySelector('#CartForm'));
  });
  window.addEventListener('pageshow', () => {
    const cartForm = document.getElementById('CartForm');
    if (typeof cartForm?._stylrSyncCartState === 'function') cartForm._stylrSyncCartState();
  });

  function initStackSection(section) {
    if (!section || section.dataset.stackReady === 'true') return;
    section.dataset.stackReady = 'true';

    const slides = Array.from(section.querySelectorAll('[data-stack-slide]'));
    const previous = section.querySelector('[data-stack-previous]');
    const next = section.querySelector('[data-stack-next]');
    const current = section.querySelector('[data-stack-current]');
    let activeSlide = 0;

    const showSlide = (index) => {
      if (slides.length < 2) return;
      activeSlide = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeSlide;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });
      if (current) current.textContent = String(activeSlide + 1);
    };

    if (previous) previous.addEventListener('click', () => showSlide(activeSlide - 1));
    if (next) next.addEventListener('click', () => showSlide(activeSlide + 1));

    const gallery = section.querySelector('[data-stack-gallery]');
    let pointerStart = null;
    if (gallery && slides.length > 1) {
      gallery.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        pointerStart = event.clientX;
      });
      gallery.addEventListener('pointerup', (event) => {
        if (pointerStart === null) return;
        const distance = event.clientX - pointerStart;
        pointerStart = null;
        if (Math.abs(distance) < 44) return;
        showSlide(activeSlide + (distance < 0 ? 1 : -1));
      });
      gallery.addEventListener('pointercancel', () => {
        pointerStart = null;
      });
    }

    const form = section.querySelector('[data-stack-form]');
    if (!form) return;

    const rows = Array.from(form.querySelectorAll('[data-stack-product]'));
    const selects = Array.from(form.querySelectorAll('[data-stack-variant]'));
    const submit = form.querySelector('[data-stack-submit]');
    const status = form.querySelector('[data-stack-status]');
    const originalTotal = form.querySelector('[data-stack-original-total]');
    const total = form.querySelector('[data-stack-total]');
    const savings = form.querySelector('[data-stack-savings]');
    const stackName = form.dataset.stackName || 'The stack';
    const stackCurrency = form.dataset.stackCurrency || 'USD';
    const readyLabel = form.dataset.readyLabel || 'add all three to cart';
    const incompleteLabel = form.dataset.incompleteLabel || 'select all three sizes';

    const selectionIsComplete = () => (
      selects.length === 3 &&
      selects.every((select) => {
        const option = select.selectedOptions[0];
        return Boolean(select.value) && option && option.dataset.available === 'true';
      })
    );

    const formatMoney = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: stackCurrency
        }).format(cents / 100);
      } catch (_) {
        return `${stackCurrency} ${(cents / 100).toFixed(2)}`;
      }
    };

    const selectedOrEstimatedPrice = (select) => {
      const selectedPrice = select.value
        ? select.selectedOptions[0]?.dataset.variantPrice
        : '';
      return Number.parseInt(selectedPrice || select.dataset.stackEstimatePrice || '0', 10);
    };

    const syncStackTotal = () => {
      if (!total) return;
      const prices = selects.map(selectedOrEstimatedPrice);
      if (prices.length !== 3 || prices.some((price) => !Number.isFinite(price) || price <= 0)) return;
      const regularCents = prices.reduce((sum, price) => sum + price, 0);
      const savingsCents = Math.min(...prices);
      const stackCents = regularCents - savingsCents;

      if (originalTotal) originalTotal.textContent = formatMoney(regularCents);
      total.textContent = formatMoney(stackCents);
      if (savings) savings.textContent = formatMoney(savingsCents);
    };

    const syncStackButton = () => {
      const complete = selectionIsComplete();
      if (submit) {
        submit.disabled = !complete;
        submit.textContent = complete ? readyLabel : incompleteLabel;
      }
      if (status) {
        status.textContent = '';
        status.classList.remove('is-error');
      }
      syncStackTotal();
    };

    const closeStackPicker = (dialog, restoreFocus = true) => {
      if (!dialog || !dialog.open) return;
      const trigger = dialog._stylrTrigger;
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      document.body.classList.remove('is-locked');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus && trigger instanceof HTMLElement) trigger.focus();
    };

    const updateDuplicateProductStates = () => {
      rows.forEach((row) => {
        const currentHandle = row.dataset.selectedProductHandle || '';
        row.querySelectorAll('[data-stack-product-option]').forEach((option) => {
          const handle = option.dataset.productHandle || '';
          const unavailable = option.dataset.productAvailable !== 'true';
          const usedElsewhere = rows.some((otherRow) => (
            otherRow !== row && otherRow.dataset.selectedProductHandle === handle
          ));
          const isCurrent = handle === currentHandle;
          const disabled = unavailable || usedElsewhere;
          const state = option.querySelector('[data-stack-option-state]');

          option.disabled = disabled;
          option.setAttribute('aria-disabled', String(disabled));
          option.setAttribute('aria-selected', String(isCurrent));
          option.classList.toggle('is-selected', isCurrent);
          if (state) {
            if (isCurrent) state.textContent = translate('currentSelection', 'Current selection');
            else if (usedElsewhere) state.textContent = translate('alreadyInStack', 'Already in your stack');
            else if (unavailable) state.textContent = translate('soldOut', 'Sold out');
            else state.textContent = translate('available', 'Available');
          }
        });
      });
    };

    const updateRowPriceFromVariant = (select) => {
      if (!select.value) return;
      const row = select.closest('[data-stack-product]');
      const price = row && row.querySelector('[data-stack-product-price]');
      const cents = Number.parseInt(select.selectedOptions[0]?.dataset.variantPrice || '0', 10);
      if (price && Number.isFinite(cents) && cents > 0) price.textContent = formatMoney(cents);
    };

    rows.forEach((row) => {
      const trigger = row.querySelector('[data-stack-picker-open]');
      const dialog = row.querySelector('[data-stack-picker]');
      const select = row.querySelector('[data-stack-variant]');
      if (!trigger || !dialog || !select) return;

      const options = Array.from(dialog.querySelectorAll('[data-stack-product-option]'));
      const close = dialog.querySelector('[data-stack-picker-close]');

      const openStackPicker = () => {
        if (dialog.open) return;
        dialog._stylrTrigger = trigger;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.body.classList.add('is-locked');
        trigger.setAttribute('aria-expanded', 'true');
        const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
        const firstAvailable = options.find((option) => !option.disabled);
        requestAnimationFrame(() => (selected || firstAvailable || close)?.focus());
      };

      trigger.addEventListener('click', openStackPicker);
      if (close) close.addEventListener('click', () => closeStackPicker(dialog));

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeStackPicker(dialog);
      });
      dialog.addEventListener('close', () => {
        document.body.classList.remove('is-locked');
        trigger.setAttribute('aria-expanded', 'false');
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeStackPicker(dialog);
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const enabledOptions = options.filter((option) => !option.disabled);
        if (!enabledOptions.length) return;
        const currentIndex = enabledOptions.indexOf(document.activeElement);
        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = enabledOptions.length - 1;
        else if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1 + enabledOptions.length) % enabledOptions.length;
        else nextIndex = (currentIndex - 1 + enabledOptions.length) % enabledOptions.length;
        event.preventDefault();
        enabledOptions[nextIndex].focus();
      });

      options.forEach((option) => {
        option.addEventListener('click', () => {
          if (option.disabled) return;
          const handle = option.dataset.productHandle || '';
          const title = option.dataset.productTitle || '';
          const template = Array.from(dialog.querySelectorAll('[data-stack-variant-template]'))
            .find((item) => item.dataset.productHandle === handle);
          if (!template) return;

          row.dataset.selectedProductHandle = handle;
          select.replaceChildren(template.content.cloneNode(true));
          select.dataset.stackProductHandle = handle;
          select.dataset.stackEstimatePrice = option.dataset.productPrice || '0';

          const currentTitle = row.querySelector('[data-stack-product-title]');
          const currentPrice = row.querySelector('[data-stack-product-price]');
          const currentImageWrap = row.querySelector('[data-stack-current-image-wrap]');
          const announcement = row.querySelector('[data-stack-product-announcement]');
          const imageUrl = option.dataset.productImage || '';

          if (currentTitle) currentTitle.textContent = title;
          if (currentPrice) currentPrice.textContent = option.dataset.productPriceLabel || '';
          if (currentImageWrap && imageUrl) {
            let image = currentImageWrap.querySelector('img');
            if (!image) {
              image = document.createElement('img');
              currentImageWrap.replaceChildren(image);
            }
            image.src = imageUrl;
            image.removeAttribute('srcset');
            image.alt = '';
            image.width = 320;
            image.height = 320;
            image.loading = 'lazy';
          }

          updateDuplicateProductStates();
          syncStackButton();
          if (announcement) {
            announcement.textContent = interpolate(
              translate('selectedChooseSize', '{{ title }} selected. Choose a size.'),
              { title }
            );
          }
          closeStackPicker(dialog);
        });
      });
    });

    selects.forEach((select) => {
      select.addEventListener('change', () => {
        updateRowPriceFromVariant(select);
        syncStackButton();
      });
    });
    updateDuplicateProductStates();
    syncStackButton();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!selectionIsComplete() || !submit) {
        if (status) {
          status.textContent = translate('chooseAllSizes', 'Choose an available size for all three pieces.');
          status.classList.add('is-error');
        }
        return;
      }

      const groupId = `${form.dataset.stackGroup || 'stack'}-${Date.now()}`;
      const items = selects.map((select) => {
        const properties = {
          _stack_group: groupId,
          _stack_name: stackName,
          _stack_role: select.dataset.stackRole || 'piece'
        };
        return {
          id: Number(select.value),
          quantity: 1,
          properties
        };
      });

      const rootUrl = (form.dataset.rootUrl || '/').replace(/\/?$/, '/');
      submit.disabled = true;
      submit.textContent = translate('addingStack', 'adding the stack…');
      if (status) {
        status.textContent = '';
        status.classList.remove('is-error');
      }

      try {
        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || translate('stackAddError', 'The stack could not be added.'));

        if (status) status.textContent = translate('stackAdded', 'All three pieces added. Opening your cart…');
        window.location.assign(`${rootUrl}cart`);
      } catch (error) {
        submit.disabled = false;
        submit.textContent = readyLabel;
        if (status) {
          status.textContent = error.message || translate('stackAddRetry', 'The stack could not be added. Please try again.');
          status.classList.add('is-error');
        }
      }
    });
  }

  document.querySelectorAll('[data-stack-section]').forEach(initStackSection);
  document.addEventListener('shopify:section:load', (event) => {
    initStackSection(event.target.querySelector('[data-stack-section]'));
  });

  function openReviewDrawer(trigger) {
    const drawer = document.querySelector('[data-product-review-drawer]');
    if (!drawer || drawer.open) return;
    drawer._stylrTrigger = trigger;
    if (typeof drawer.showModal === 'function') drawer.showModal();
    else drawer.setAttribute('open', '');
    document.body.classList.add('is-locked');
    document.querySelectorAll('[data-review-drawer-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
    });
    const close = drawer.querySelector('[data-review-drawer-close]');
    if (close) close.focus();
  }

  function closeReviewDrawer(drawer, restoreFocus = true) {
    if (!drawer || !drawer.open) return;
    if (typeof drawer.close === 'function') drawer.close();
    else drawer.removeAttribute('open');
    document.body.classList.remove('is-locked');
    document.querySelectorAll('[data-review-drawer-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
    if (restoreFocus && drawer._stylrTrigger instanceof HTMLElement) drawer._stylrTrigger.focus();
  }

  document.addEventListener('click', (event) => {
    const previewTrigger = event.target.closest(
      '[data-review-preview] a, [data-review-preview] .jdgm-prev-badge'
    );
    if (!previewTrigger) return;
    event.preventDefault();
    event.stopPropagation();
    openReviewDrawer(previewTrigger);
  }, true);

  document.addEventListener('click', (event) => {
    const reviewTrigger = event.target.closest('[data-review-drawer-open]');
    if (reviewTrigger) {
      event.preventDefault();
      openReviewDrawer(reviewTrigger);
      return;
    }

    const reviewClose = event.target.closest('[data-review-drawer-close]');
    if (reviewClose) closeReviewDrawer(reviewClose.closest('[data-product-review-drawer]'));
  });

  document.querySelectorAll('[data-product-review-drawer]').forEach((drawer) => {
    const fallback = drawer.querySelector('[data-review-empty]');
    const widget = drawer.querySelector('.jdgm-review-widget');
    const syncReviewEmptyState = () => {
      if (!fallback || !widget) return;
      fallback.hidden = Boolean(widget.textContent.trim());
    };
    syncReviewEmptyState();
    if (widget) {
      const reviewObserver = new MutationObserver(syncReviewEmptyState);
      reviewObserver.observe(widget, { childList: true, subtree: true, characterData: true });
    }
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) closeReviewDrawer(drawer);
    });
    drawer.addEventListener('close', () => {
      document.body.classList.remove('is-locked');
      document.querySelectorAll('[data-review-drawer-open]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
    });
  });

  async function initProductRecommendations(section) {
    if (!section || section.dataset.recommendationsReady === 'true') return;
    section.dataset.recommendationsReady = 'true';
    const url = section.dataset.url;
    if (!url) return;

    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-product-recommendations]');
      if (!replacement || replacement.hidden || !replacement.innerHTML.trim()) return;
      section.innerHTML = replacement.innerHTML;
      section.hidden = false;
    } catch (error) {
      section.hidden = true;
    }
  }

  const recentlyViewedKey = 'stylr:recently-viewed:v1';

  function readRecentlyViewed() {
    try {
      const value = JSON.parse(window.localStorage.getItem(recentlyViewedKey) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  }

  function writeRecentlyViewed(items) {
    try {
      window.localStorage.setItem(recentlyViewedKey, JSON.stringify(items));
    } catch (error) {
      // Browsing history remains optional when storage is unavailable.
    }
  }

  function buildRecentProductCard(item) {
    const article = document.createElement('article');
    article.className = 'product-card';

    const top = document.createElement('div');
    top.className = 'product-card-top';
    if (item.type) {
      const type = document.createElement('small');
      type.textContent = item.type;
      top.append(type);
    }
    article.append(top);

    const media = document.createElement('a');
    media.className = 'product-card-media';
    media.href = item.url;
    media.setAttribute('aria-label', interpolate(
      translate('viewProduct', 'View {{ title }}'),
      { title: item.title }
    ));
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 900;
      image.height = 900;
      media.append(image);
    }
    article.append(media);

    const copy = document.createElement('div');
    copy.className = 'product-card-copy';
    const details = document.createElement('div');
    const title = document.createElement('a');
    title.href = item.url;
    title.textContent = item.title;
    details.append(title);
    const price = document.createElement('b');
    price.textContent = item.price;
    details.append(price);
    copy.append(details);

    const choose = document.createElement('a');
    choose.className = 'quick-add';
    choose.href = item.url;
    choose.setAttribute('aria-label', interpolate(
      translate('chooseOptions', 'Choose options for {{ title }}'),
      { title: item.title }
    ));
    const plus = document.createElement('span');
    plus.setAttribute('aria-hidden', 'true');
    plus.textContent = '+';
    choose.append(plus);
    copy.append(choose);
    article.append(copy);
    return article;
  }

  function initRecentlyViewed(section) {
    if (!section || section.dataset.recentlyReady === 'true') return;
    section.dataset.recentlyReady = 'true';
    const current = {
      id: section.dataset.productId,
      url: section.dataset.productUrl,
      title: section.dataset.productTitle,
      type: section.dataset.productType,
      price: section.dataset.productPrice,
      image: section.dataset.productImage || ''
    };
    if (!current.id || !current.url || !current.title) return;

    const stored = readRecentlyViewed().filter((item) => item && item.id && item.id !== current.id);
    const limit = Number.parseInt(section.dataset.limit || '4', 10);
    const visible = stored.slice(0, Number.isFinite(limit) ? limit : 4);
    const list = section.querySelector('[data-recently-viewed-list]');
    if (list && visible.length) {
      visible.forEach((item) => list.append(buildRecentProductCard(item)));
      section.hidden = false;
    }

    const updated = [current, ...stored].filter((item, index, items) => (
      items.findIndex((candidate) => candidate.id === item.id) === index
    )).slice(0, 8);
    writeRecentlyViewed(updated);
  }

  function initStickyAtc(section) {
    if (!section || section.dataset.stickyAtcReady === 'true') return;
    section.dataset.stickyAtcReady = 'true';
    const bar = section.querySelector('[data-sticky-atc]');
    const mainButton = section.querySelector('[data-add-button]');
    const stickyButton = section.querySelector('[data-sticky-atc-button]');
    if (!bar || !mainButton || !stickyButton) return;

    stickyButton.addEventListener('click', () => {
      const form = mainButton.form;
      const selection = form && form.stylrProductSelection;
      if (selection) {
        const state = selection.refresh();
        if (!state.complete || !state.available) {
          selection.focusMissing();
          return;
        }
      }
      mainButton.click();
    });

    let mainButtonIsVisible = true;
    let footerIsVisible = false;
    const syncVisibility = () => {
      const shouldHide = mainButtonIsVisible || footerIsVisible;
      bar.classList.toggle('is-hidden', shouldHide);
      bar.setAttribute('aria-hidden', String(shouldHide));
    };

    const observer = new IntersectionObserver((entries) => {
      mainButtonIsVisible = entries.some((entry) => entry.isIntersecting);
      syncVisibility();
    }, { threshold: 0.15 });
    observer.observe(mainButton);

    const footer = document.querySelector('.site-footer');
    if (footer) {
      const footerObserver = new IntersectionObserver((entries) => {
        footerIsVisible = entries.some((entry) => entry.isIntersecting);
        syncVisibility();
      }, { threshold: 0 });
      footerObserver.observe(footer);
    }
  }

  document.querySelectorAll('[data-product-recommendations]').forEach(initProductRecommendations);
  document.querySelectorAll('[data-recently-viewed]').forEach(initRecentlyViewed);
  document.querySelectorAll('[data-product-section]').forEach(initStickyAtc);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-form]').forEach(initProductVariantForm);
    initProductRecommendations(event.target.querySelector('[data-product-recommendations]'));
    initRecentlyViewed(event.target.querySelector('[data-recently-viewed]'));
    initStickyAtc(event.target.querySelector('[data-product-section]'));
  });

})();
